import type { CustomField, CustomFieldsResponse } from "@/schema"
import { useMutation, useQueryClient } from "@tanstack/react-query"
import { toast } from "sonner"

import { useApiContext } from "@/hooks/api/use-api-context"

import { createAiFill, createCustomField, deleteCustomField, reorderCustomFields, updateCustomField } from "./api"

export function useCreateCustomField() {
  const getContext = useApiContext()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (customField: Partial<CustomField>) => {
      const context = await getContext()
      if (!context) throw new Error("No context found")
      return createCustomField(context, customField)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["custom_fields"] })
    },
  })
}

export function useUpdateCustomField() {
  const getContext = useApiContext()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ id, customField }: { id: string; customField: Partial<CustomField> }) => {
      const context = await getContext()
      if (!context) throw new Error("No context found")
      return updateCustomField(context, id, customField)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["custom_fields"] })
      queryClient.invalidateQueries({ queryKey: ["custom_field"] })
    },
  })
}

export function useDeleteCustomField() {
  const getContext = useApiContext()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (id: string) => {
      const context = await getContext()
      if (!context) throw new Error("No context found")
      return deleteCustomField(context, id)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["custom_fields"] })
    },
  })
}

interface ReorderVariables {
  entityType: string
  orderedFieldIds: string[]
}

export function useReorderCustomFields() {
  const getContext = useApiContext()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ entityType, orderedFieldIds }: ReorderVariables) => {
      const context = await getContext()
      if (!context) throw new Error("No context found")
      await reorderCustomFields(context, entityType, orderedFieldIds)
    },
    onMutate: async ({ entityType, orderedFieldIds }) => {
      await queryClient.cancelQueries({ queryKey: ["custom_fields"] })

      const indexById = new Map(orderedFieldIds.map((id, index) => [id, index]))
      const snapshots = queryClient.getQueriesData<CustomFieldsResponse>({ queryKey: ["custom_fields"] })

      snapshots.forEach(([key, old]) => {
        if (!old) return
        const updated = old.custom_fields.map((field) => {
          if (field.entity_type !== entityType) return field
          const nextIndex = indexById.get(field.id)
          return nextIndex === undefined ? field : { ...field, sort_order: nextIndex }
        })
        const sorted = [...updated].sort((a, b) => {
          if (a.sort_order !== b.sort_order) return a.sort_order - b.sort_order
          return a.display_name.localeCompare(b.display_name)
        })
        queryClient.setQueryData<CustomFieldsResponse>(key, { ...old, custom_fields: sorted })
      })

      return { snapshots }
    },
    onSuccess: () => {
      toast.success("Custom field order saved")
    },
    onError: (_error, _variables, context) => {
      context?.snapshots.forEach(([key, data]) => {
        if (data) queryClient.setQueryData(key, data)
      })
    },
    meta: { errorToast: "Failed to save custom field order. Please try again." },
    // No onSettled invalidation: GET requests route to the read replica
    // (see backend/config/initializers/multi_db.rb) which lags behind the
    // primary by a beat and would return stale sort_order values, making
    // the drop look like it snapped back. The optimistic cache already
    // matches what we just wrote, so there's nothing to reconcile.
  })
}

export function useAiFillCustomField() {
  const getContext = useApiContext()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({
      customFieldId,
      prompt,
      overwrite,
      fillForRecordType,
    }: {
      customFieldId: string
      prompt: string
      overwrite: boolean
      fillForRecordType: CustomField["fill_for_record_type"]
    }) => {
      const context = await getContext()
      if (!context) throw new Error("No context found")
      return createAiFill(context, customFieldId, {
        prompt,
        overwrite,
        fill_for_record_type: fillForRecordType,
      })
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["custom_fields"] })
      queryClient.invalidateQueries({ queryKey: ["custom_field"] })
    },
    meta: { errorToast: "Failed to save AI fill settings. Please try again." },
  })
}
